import { TripViewSchema, type TripView } from '@voll-intelligence/domain';
import { z } from 'zod';
import type { HttpService } from '@web/data/services';

/**
 * Wire of kanban `GET /orgs/:orgId/customers/:customerId/trips/:tripId` — the
 * trip-api `TripAppView` proxied verbatim. `ownerPublicId` is the kanban guard's
 * business; the UI only needs the `TripView`.
 */
const TripAppViewResponseSchema = z.object({
  trip: TripViewSchema,
  ownerPublicId: z.string(),
});

const CustomerTripsResponseSchema = z.object({ trips: z.array(TripViewSchema) });

export interface TripRepository {
  /** The app-facing TripView of one trip of a traveler bound to a card in the org. 404 → HttpError(404). */
  getCustomerTrip: (orgId: string, customerId: string, tripId: string) => Promise<TripView>;
  /** A traveler's trips, already ordered by the kanban (live first, then past; at most 20). 404 → HttpError(404). */
  listCustomerTrips: (orgId: string, customerId: string) => Promise<TripView[]>;
}

export function createTripRepository(http: HttpService): TripRepository {
  return {
    getCustomerTrip: async (orgId, customerId, tripId) => {
      const out = await http.get(
        `/orgs/${encodeURIComponent(orgId)}/customers/${encodeURIComponent(customerId)}/trips/${encodeURIComponent(tripId)}`,
        { schema: TripAppViewResponseSchema },
      );
      return out.trip;
    },
    listCustomerTrips: async (orgId, customerId) => {
      const out = await http.get(
        `/orgs/${encodeURIComponent(orgId)}/customers/${encodeURIComponent(customerId)}/trips`,
        { schema: CustomerTripsResponseSchema },
      );
      return out.trips;
    },
  };
}
